// ===============================================================================
// Dashboard.js - Groups List & Summary Dashboard for WOW
// ===============================================================================
// Location: /var/www/wow/frontend/src/Dashboard.js
// Shows all user's groups and overall balance summary
// ===============================================================================

import React, { useState, useEffect } from "react";
import { groupsAPI, formatCurrency } from "./api";
import "./Dashboard.css";

function Dashboard({ user, onSelectGroup, onCreateGroup, onJoinGroup, onUpgrade }) {
  // ===============================================================================
  // State
  // ===============================================================================
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [premiumDaysRemaining, setPremiumDaysRemaining] = useState(null);

  // ===============================================================================
  // Load Groups
  // ===============================================================================
  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await groupsAPI.list();
      if (res.data?.ok) {
        setGroups(res.data.groups || []);
      }
    } catch (err) {
      console.error("Load groups error:", err);
      setError("Failed to load groups");
    }
    setLoading(false);
  };

  // ===============================================================================
  // Calculate Premium Days Remaining
  // ===============================================================================
  useEffect(() => {
    if (user?.is_premium && user?.premium_expires_at) {
      const expiryDate = new Date(user.premium_expires_at);
      const now = new Date();
      const diffTime = expiryDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setPremiumDaysRemaining(diffDays);
    } else {
      setPremiumDaysRemaining(null);
    }
  }, [user]);

  // ===============================================================================
  // Render Loading
  // ===============================================================================
  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-loading">
          <div className="spinner"></div>
          <p>Loading your groups...</p>
        </div>
      </div>
    );
  }

  // ===============================================================================
  // Render
  // ===============================================================================
  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Welcome, {user?.name || "User"}! 👋</h1>
          <p>Manage your shared expenses with friends and family</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={onCreateGroup}>
            ➕ Create Group
          </button>
          <button className="btn btn-primary" onClick={onJoinGroup}>
            🙋‍♂️ Join Group
          </button>
        </div>
      </div>
      {/* Premium Expiry Warning Banner - Shows 10 days before expiry */}
      {user?.is_premium && premiumDaysRemaining !== null && premiumDaysRemaining <= 10 && premiumDaysRemaining > 0 && (
        <div className="premium-expiry-banner">
          <div className="expiry-banner-content">
            <span className="expiry-icon">⏰</span>
            <div className="expiry-text">
              <strong>Your premium trial expires in {premiumDaysRemaining} day{premiumDaysRemaining !== 1 ? 's' : ''}!</strong>
              <span>Upgrade now to keep your premium features.</span>
            </div>
          </div>
          <button className="btn btn-upgrade-now" onClick={onUpgrade}>
            Upgrade Now
          </button>
        </div>
      )}

      {/* Premium Expired Banner */}
      {user?.is_premium && premiumDaysRemaining !== null && premiumDaysRemaining <= 0 && (
        <div className="premium-expiry-banner expired">
          <div className="expiry-banner-content">
            <span className="expiry-icon">⚠️</span>
            <div className="expiry-text">
              <strong>Your premium trial has expired!</strong>
              <span>Upgrade now to restore your premium features.</span>
            </div>
          </div>
          <button className="btn btn-upgrade-now" onClick={onUpgrade}>
            Upgrade Now
          </button>
        </div>
      )}
      {/* Error */}
      {error && (
        <div className="dashboard-error">
          <p>{error}</p>
          <button onClick={loadGroups}>Try Again</button>
        </div>
      )}

      {/* Empty State */}
      {!error && groups.length === 0 && (
        <div className="dashboard-empty">
          <div className="empty-icon">📋</div>
          <h2>No Groups Yet</h2>
          <p>Create a new group or join an existing one to start tracking expenses</p>
          <div className="empty-actions">
            <button className="btn btn-primary" onClick={onCreateGroup}>
              ➕ Create Your First Group
            </button>
            <button className="btn btn-secondary" onClick={onJoinGroup}>
              🔗 Join with Invite Code
            </button>
          </div>
        </div>
      )}

      {/* Groups Grid */}
      {groups.length > 0 && (
        <div className="groups-section">
          <h2 className="section-title">Your Groups ({groups.length})</h2>
          <div className="groups-grid">
            {groups.map((group) => (
              <div 
                key={group.id} 
                className="group-card"
                onClick={() => onSelectGroup(group.id)}
              >
                <div className="group-card-header">
                  <div className="group-icon">
                    {group.name.charAt(0).toUpperCase()}
                  </div>
                  <h3 className="group-name">{group.name}</h3>
                </div>
                
                {group.description && (
                  <p className="group-description">{group.description}</p>
                )}
                
                <div className="group-card-footer">
                  <span className="group-members">
                    👥 {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                  </span>
                  <span className="group-currency">{group.currency}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Premium Banner (if not premium and has 2+ groups) */}
      {!user?.is_premium && groups.length >= 2 && (
        <div className="premium-banner">
          <div className="premium-icon">⭐</div>
          <div className="premium-content">
            <h3>Upgrade to Premium</h3>
            <p>Get unlimited groups, receipt photos, and more for just €1/month</p>
          </div>
          <button className="btn btn-premium" onClick={onUpgrade}>Upgrade Now</button>
        </div>
      )}
    </div>
  );
}

export default Dashboard;

